import DefaultPending from '@/components/DefaultPending'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import {
  OrderChangeCartPanel,
  orderChangeLocationQuery,
} from '@/components/pos/OrderChangeCartPanel'
import { PosCompletionDialog } from '@/components/pos/PosCompletionDialog'
import { PosWorkspaceLayout } from '@/components/pos/PosWorkspaceLayout'
import { ProductCatalog } from '@/components/pos/ProductCatalog'
import { Button } from '@/components/ui/button'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { adminTitleHead } from '@/lib/admin-route-title'
import { isOrderQuoteLocked } from '@/lib/order-quote'
import { createPosCartActions, usePosCartWithPendingAdds } from '@/lib/pos/cart-actions'
import { canChangeSalesOrderItems } from '@/lib/sales-order-access'
import { cartQueries, useIsCartOrderEnding, usePosCart } from '@/lib/services/carts/carts.query'
import { categoryQueries } from '@/lib/services/categories/categories.query'
import { salesOrderSchema } from '@/lib/services/sales-orders/sales-order.schema'
import { useOpenSalesOrderEditCartMutation } from '@/lib/services/sales-orders/sales-order-changes.query'
import { salesOrderEditSearchSchema } from '@/lib/services/sales-orders/sales-order-changes.schema'
import { salesOrderQueries } from '@/lib/services/sales-orders/sales-orders.query'
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { ArrowLeft, PencilLine } from 'lucide-react'
import { getErrorMessage } from '@/lib/utils'
import { useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_authenticated/$orgId/sales-orders/$salesOrderId_/edit')({
  component: EditSalesOrder,
  params: {
    parse: (params) => salesOrderSchema.pick({ salesOrderId: true }).parse(params),
  },
  validateSearch: (search) => salesOrderEditSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ cartId: search.cartId }),
  // After validateSearch and loaderDeps, so TypeScript infers their types before head's.
  head: ({ params }) => adminTitleHead('Edit', `Order #${params.salesOrderId}`, 'Orders'),
  loader: async ({ context, deps, params }) => {
    if (!canChangeSalesOrderItems(context.ability)) throw notFound()

    // A change saved or discarded has no draft left (404): the screen shows the change's completion instead.
    const cart = deps.cartId
      ? await context.queryClient
          .ensureQueryData(
            cartQueries.posShow({ organizationId: params.orgId, cartId: deps.cartId })
          )
          .catch((error: unknown) => {
            if (isAxiosError(error) && error.response?.status === 404) return null
            throw error
          })
      : null

    await Promise.all([
      context.queryClient.ensureQueryData(
        categoryQueries.index({ organizationId: params.orgId, filters: { sort: 'pos' } })
      ),
      // The breadcrumb names the order from this cache entry.
      context.queryClient.ensureQueryData(
        salesOrderQueries.show({ organizationId: params.orgId, salesOrderId: params.salesOrderId })
      ),
      // The order's location, which the cart's pickup and delivery dialogs read.
      cart?.location_id
        ? context.queryClient.ensureQueryData(
            orderChangeLocationQuery(params.orgId, cart.location_id)
          )
        : null,
    ])

    return { isReadOnly: false }
  },
})

/** The POS screen editing an issued order: the same catalog and cart, saved as a change to the order. */
function EditSalesOrder() {
  const { cartId } = Route.useSearch()
  const organizationId = useRouteOrganizationId()
  const { salesOrderId } = Route.useParams()
  const path = usePathHelper()
  const navigate = useNavigate()
  const openEditCart = useOpenSalesOrderEditCartMutation()
  const isCartOrderEnding = useIsCartOrderEnding()
  // Catalog adds queue here, so this screen's draft and its unsaved adds outlive catalog renders.
  const [cartActions] = useState(createPosCartActions)
  // The change's Pay Now stays in front of its completion: the payment that saves the change can be followed by another.
  const [isPayNowOpen, setIsPayNowOpen] = useState(false)
  // An empty `cartId` in the URL names no draft.
  const { data: cart, isLoading } = usePosCart(cartId || null, {
    organizationId,
    enabled: Boolean(cartId),
  })
  const displayCart = usePosCartWithPendingAdds(cartActions, cart ?? null)
  const orderLink = path.salesOrders.show(salesOrderId).using({ orgId: organizationId }).link
  const orderChange = displayCart?.order_change ?? null
  // The draft already exists; nothing on this screen may create a different cart.
  const createCartWithDefaults = async () => {
    if (!cart) {
      throw new Error('This change is no longer open.')
    }

    return cart
  }

  // A new change to the order, opened as the order page's Edit Order opens one.
  const startNewEdit = async () => {
    try {
      const response = await openEditCart.mutateAsync({ salesOrderId })
      await navigate({
        ...path.salesOrders.edit(salesOrderId).search({ cartId: response.cart_id }).link,
        replace: true,
      })
    } catch (error) {
      toast.error('Unable to change this order', { description: getErrorMessage(error) })
    }
  }

  if (isLoading) return <DefaultPending />

  const header = (
    <AdminPageHeader
      icon={PencilLine}
      title={`Edit Order #${orderChange?.order_number ?? salesOrderId}`}
      description="Change items, units, prices and discounts, or the order's details. Kept items keep their original prices."
      density="compact"
      actions={
        <Button asChild variant="outline" size="sm">
          <Link {...orderLink}>
            <ArrowLeft className="size-4" />
            Back to order
          </Link>
        </Button>
      }
    />
  )

  // A change saved or discarded (opened again later, or closed elsewhere while open) shows its completion over the
  // draft as it was left, as a POS cart shows a finished sale: staff leave by viewing the order or starting a new one.
  const closed = orderChange?.closed ?? null
  const completion = (
    <PosCompletionDialog
      open
      icon={<PencilLine className="h-5 w-5" />}
      title={
        closed === 'saved'
          ? 'Change Saved'
          : closed === 'discarded'
            ? 'Change Discarded'
            : 'Change Closed'
      }
      description={`${
        closed === 'saved'
          ? 'This change was saved to the order.'
          : closed === 'discarded'
            ? 'This change was discarded.'
            : 'This change was saved or discarded.'
      } View the order, or start a new edit.`}
      busy={openEditCart.isPending}
      onViewOrder={() => void navigate({ ...orderLink, replace: true })}
      start={{
        label: 'Start New Edit',
        icon: <PencilLine className="h-4 w-4" />,
        onClick: () => void startNewEdit(),
      }}
    />
  )
  if (!displayCart || !orderChange || Number(orderChange.sales_order_id) !== Number(salesOrderId)) {
    return (
      <>
        {header}
        {completion}
      </>
    )
  }
  const isClosed = closed !== null

  const catalog = (
    <ProductCatalog
      cart={displayCart}
      cartId={displayCart.id}
      cartActions={cartActions}
      createCartWithDefaults={createCartWithDefaults}
      // The cart's lock covers a closed change and one held by a payment toward it.
      disabled={isCartOrderEnding || isOrderQuoteLocked(displayCart)}
      className="lg:h-full lg:min-h-0"
    />
  )
  const cartPanel = (
    <OrderChangeCartPanel
      cart={displayCart}
      createCartWithDefaults={createCartWithDefaults}
      onPayNowOpenChange={setIsPayNowOpen}
      className="lg:h-full lg:min-h-0"
    />
  )

  return (
    <>
      <PosWorkspaceLayout header={header} catalog={catalog} cart={cartPanel} />
      {isClosed && !isPayNowOpen ? completion : null}
    </>
  )
}
